/**
 * Smart warehouse layout algorithm.
 * Generates intelligent rack placement using real warehouse patterns
 * (back-to-back rows, parallel rows, perimeter, U-shape, L-shape).
 *
 * Replaces the naive findOptimalPosition() grid-search with a
 * one-pass algorithm that understands aisles, rows, and rack grouping.
 */

import {
  RackPosition,
  RackDimensions,
  RackSummary,
  WarehouseDimensions,
  SmartLayoutConfig,
  LayoutMetrics,
  LayoutTemplate,
  LayoutTemplateName,
  PreviewRackPosition,
} from '../types/warehouseLayout';
import {
  snapToGrid,
  getRotatedDimensions,
} from './positionCalculator';
import { validatePreviewPositions } from './bulkPlacement';
import { calculateRackDimensions } from './positionCalculator';

// ============================================
// Layout Templates
// ============================================

export const LAYOUT_TEMPLATES: LayoutTemplate[] = [
  {
    name: 'back-to-back',
    label: 'Rygg-mot-rygg',
    description: 'Mest plasseffektivt. Reoler plasseres parvis rygg-mot-rygg med ganger mellom.',
    icon: 'Rows4',
    defaultConfig: { backToBack: true, aisleWidth: 2.3, rackSpacing: 0.5, backToBackGap: 0.2 },
  },
  {
    name: 'parallel-rows',
    label: 'Parallelle rader',
    description: 'Enkle rader med gang mellom hver rad. God tilgjengelighet.',
    icon: 'Rows3',
    defaultConfig: { backToBack: false, aisleWidth: 2.5, rackSpacing: 0.5 },
  },
  {
    name: 'u-shape',
    label: 'U-form',
    description: 'Reoler langs 3 vegger med åpen inngang. For mindre lager.',
    icon: 'SquareBottomDashedScissors',
    defaultConfig: { backToBack: false, wallMargin: 0.8, aisleWidth: 2.3 },
  },
  {
    name: 'l-shape',
    label: 'L-form',
    description: 'Reoler langs 2 tilstøtende vegger. For hjørnelager.',
    icon: 'CornerDownRight',
    defaultConfig: { backToBack: false, wallMargin: 0.8, aisleWidth: 2.3 },
  },
  {
    name: 'perimeter',
    label: 'Perimeter',
    description: 'Reoler langs alle 4 vegger med åpent midtområde.',
    icon: 'Square',
    defaultConfig: { backToBack: false, wallMargin: 0.8, rackSpacing: 0.5, aisleWidth: 2.3 },
  },
];

// ============================================
// Rack Grouping (by dimensions)
// ============================================

export interface RackGroup {
  racks: RackSummary[];
  avgWidth: number;
  avgDepth: number;
}

/**
 * Groups racks by similar physical dimensions for uniform row placement.
 * Racks within ±0.5m width and ±0.2m depth tolerance are grouped together.
 */
export function groupRacksByDimensions(
  racks: RackSummary[],
  dimensionsMap: Map<string, RackDimensions>
): RackGroup[] {
  const groups: RackGroup[] = [];

  for (const rack of racks) {
    const dim = dimensionsMap.get(rack.rackNumber);
    if (!dim) continue;

    let placed = false;
    for (const group of groups) {
      if (Math.abs(dim.width - group.avgWidth) < 0.5 && Math.abs(dim.depth - group.avgDepth) < 0.2) {
        group.racks.push(rack);
        // Recalculate average
        const total = group.racks.length;
        group.avgWidth = group.racks.reduce((sum, r) => {
          const d = dimensionsMap.get(r.rackNumber);
          return sum + (d ? d.width : 0);
        }, 0) / total;
        group.avgDepth = group.racks.reduce((sum, r) => {
          const d = dimensionsMap.get(r.rackNumber);
          return sum + (d ? d.depth : 0);
        }, 0) / total;
        placed = true;
        break;
      }
    }

    if (!placed) {
      groups.push({
        racks: [rack],
        avgWidth: dim.width,
        avgDepth: dim.depth,
      });
    }
  }

  // Sort groups: largest groups first, then sort racks within by rackNumber
  groups.sort((a, b) => b.racks.length - a.racks.length);
  for (const group of groups) {
    group.racks.sort((a, b) => a.rackNumber.localeCompare(b.rackNumber, undefined, { numeric: true }));
  }

  return groups;
}

// ============================================
// Row-Based Layout Algorithms
// ============================================

/**
 * Generate a complete warehouse layout using the specified template and configuration.
 * Returns preview positions (with validation) and layout metrics.
 */
export function generateWarehouseLayout(
  racksToPlace: RackSummary[],
  existingRacks: RackPosition[],
  dimensionsMap: Map<string, RackDimensions>,
  warehouseDimensions: WarehouseDimensions,
  config: SmartLayoutConfig,
  gridUnit: number
): { positions: PreviewRackPosition[]; metrics: LayoutMetrics } {
  if (racksToPlace.length === 0) {
    return {
      positions: [],
      metrics: { totalRacks: 0, placedRacks: 0, failedRacks: 0, spaceUtilization: 0, aisleCount: 0, rowCount: 0 },
    };
  }

  let rawPositions: RackPosition[];

  switch (config.template) {
    case 'back-to-back':
    case 'parallel-rows':
      rawPositions = generateRowLayout(racksToPlace, dimensionsMap, warehouseDimensions, config, gridUnit);
      break;
    case 'u-shape':
      rawPositions = generatePerimeterLayout(racksToPlace, dimensionsMap, warehouseDimensions, config, gridUnit, 3);
      break;
    case 'l-shape':
      rawPositions = generatePerimeterLayout(racksToPlace, dimensionsMap, warehouseDimensions, config, gridUnit, 2);
      break;
    case 'perimeter':
      rawPositions = generatePerimeterLayout(racksToPlace, dimensionsMap, warehouseDimensions, config, gridUnit, 4);
      break;
    default:
      rawPositions = generateRowLayout(racksToPlace, dimensionsMap, warehouseDimensions, config, gridUnit);
  }

  // Convert to preview positions for validation
  const previews: PreviewRackPosition[] = rawPositions.map(p => ({
    ...p,
    isValid: true,
  }));

  // Validate against existing racks and each other
  const validated = validatePreviewPositions(
    previews,
    existingRacks,
    dimensionsMap,
    warehouseDimensions,
    0.3
  );

  const metrics = calculateLayoutMetrics(validated, racksToPlace.length, warehouseDimensions, dimensionsMap, config);

  return { positions: validated, metrics };
}

// ============================================
// Row-based layout (back-to-back & parallel)
// ============================================

function generateRowLayout(
  racksToPlace: RackSummary[],
  dimensionsMap: Map<string, RackDimensions>,
  warehouseDimensions: WarehouseDimensions,
  config: SmartLayoutConfig,
  gridUnit: number
): RackPosition[] {
  const positions: RackPosition[] = [];
  const groups = groupRacksByDimensions(racksToPlace, dimensionsMap);

  const isHorizontal = config.orientation === 'horizontal';
  const usableLength = (isHorizontal ? warehouseDimensions.length : warehouseDimensions.width) - 2 * config.wallMargin;
  const usableWidth = (isHorizontal ? warehouseDimensions.width : warehouseDimensions.length) - 2 * config.wallMargin;

  let currentRowY = config.wallMargin; // Tracks Y progress (stacking direction)

  for (const group of groups) {
    const rackDepth = group.avgDepth;
    const rackWidth = group.avgWidth;

    // Calculate how many racks fit per row
    const racksPerRow = Math.max(1, Math.floor((usableLength + config.rackSpacing) / (rackWidth + config.rackSpacing)));

    // Calculate row pitch (distance between row start positions)
    let rowPitch: number;
    if (config.backToBack) {
      // Back-to-back: two rack depths + back-to-back gap + aisle
      rowPitch = rackDepth * 2 + config.backToBackGap + config.aisleWidth;
    } else {
      // Single row: one rack depth + aisle
      rowPitch = rackDepth + config.aisleWidth;
    }

    let rackIndex = 0;
    while (rackIndex < group.racks.length) {
      // Check if there's space for another row pair/single row
      const rowSpaceNeeded = config.backToBack ? rackDepth * 2 + config.backToBackGap : rackDepth;
      if (currentRowY + rowSpaceNeeded > (isHorizontal ? warehouseDimensions.width : warehouseDimensions.length) - config.wallMargin) {
        break; // No more vertical space
      }

      // Place racks in the first row of this pair
      const racksInFirstRow = Math.min(racksPerRow, group.racks.length - rackIndex);
      for (let i = 0; i < racksInFirstRow; i++) {
        const rack = group.racks[rackIndex + i];
        const dim = dimensionsMap.get(rack.rackNumber);
        if (!dim) continue;

        // Position along the row direction
        const posAlongRow = config.wallMargin + i * (rackWidth + config.rackSpacing) + rackWidth / 2;
        const posAcrossRow = currentRowY + rackDepth / 2;

        const x = isHorizontal ? posAlongRow : posAcrossRow;
        const y = isHorizontal ? posAcrossRow : posAlongRow;

        positions.push({
          rackNumber: rack.rackNumber,
          x: snapToGrid(x, gridUnit),
          y: snapToGrid(y, gridUnit),
          rotation: isHorizontal ? 0 : 90,
        });
      }
      rackIndex += racksInFirstRow;

      // Place racks in the second row (back-to-back, facing opposite direction)
      if (config.backToBack && rackIndex < group.racks.length) {
        const secondRowY = currentRowY + rackDepth + config.backToBackGap + rackDepth / 2;
        const racksInSecondRow = Math.min(racksPerRow, group.racks.length - rackIndex);

        for (let i = 0; i < racksInSecondRow; i++) {
          const rack = group.racks[rackIndex + i];
          const dim = dimensionsMap.get(rack.rackNumber);
          if (!dim) continue;

          const posAlongRow = config.wallMargin + i * (rackWidth + config.rackSpacing) + rackWidth / 2;
          const posAcrossRow = secondRowY;

          const x = isHorizontal ? posAlongRow : posAcrossRow;
          const y = isHorizontal ? posAcrossRow : posAlongRow;

          // 180° rotation for second row (facing opposite direction)
          const baseRotation: 0 | 90 | 180 | 270 = isHorizontal ? 180 : 270;

          positions.push({
            rackNumber: rack.rackNumber,
            x: snapToGrid(x, gridUnit),
            y: snapToGrid(y, gridUnit),
            rotation: baseRotation,
          });
        }
        rackIndex += racksInSecondRow;
      }

      currentRowY += rowPitch;
    }
  }

  return positions;
}

// ============================================
// Perimeter-based layout (U-shape, L-shape, perimeter)
// ============================================

function generatePerimeterLayout(
  racksToPlace: RackSummary[],
  dimensionsMap: Map<string, RackDimensions>,
  warehouseDimensions: WarehouseDimensions,
  config: SmartLayoutConfig,
  gridUnit: number,
  walls: 2 | 3 | 4 // How many walls to place racks along
): RackPosition[] {
  const positions: RackPosition[] = [];
  const margin = config.wallMargin;
  const spacing = config.rackSpacing;

  // Determine which walls to use (clockwise from top: top, right, bottom, left)
  // U-shape (3): top, right, left (open bottom for entrance)
  // L-shape (2): top, left
  // Perimeter (4): all walls
  interface WallSegment {
    startX: number;
    startY: number;
    direction: 'horizontal' | 'vertical';
    facingRotation: 0 | 90 | 180 | 270; // Rotation so rack faces inward
    maxLength: number;
  }

  const wallSegments: WallSegment[] = [];

  // Top wall (racks face down, rotation 0)
  if (walls >= 2) {
    wallSegments.push({
      startX: margin,
      startY: margin,
      direction: 'horizontal',
      facingRotation: 0,
      maxLength: warehouseDimensions.length - 2 * margin,
    });
  }

  if (walls === 2) {
    // L-shape: top + left
    wallSegments.push({
      startX: margin,
      startY: margin,
      direction: 'vertical',
      facingRotation: 90,
      maxLength: warehouseDimensions.width - 2 * margin,
    });
  } else if (walls === 3) {
    // U-shape: top + right + left
    wallSegments.push({
      startX: warehouseDimensions.length - margin,
      startY: margin,
      direction: 'vertical',
      facingRotation: 270,
      maxLength: warehouseDimensions.width - 2 * margin,
    });
    wallSegments.push({
      startX: margin,
      startY: margin,
      direction: 'vertical',
      facingRotation: 90,
      maxLength: warehouseDimensions.width - 2 * margin,
    });
  } else {
    // Perimeter: all 4 walls
    wallSegments.push({
      startX: warehouseDimensions.length - margin,
      startY: margin,
      direction: 'vertical',
      facingRotation: 270,
      maxLength: warehouseDimensions.width - 2 * margin,
    });
    wallSegments.push({
      startX: margin,
      startY: warehouseDimensions.width - margin,
      direction: 'horizontal',
      facingRotation: 180,
      maxLength: warehouseDimensions.length - 2 * margin,
    });
    wallSegments.push({
      startX: margin,
      startY: margin,
      direction: 'vertical',
      facingRotation: 90,
      maxLength: warehouseDimensions.width - 2 * margin,
    });
  }

  let rackIndex = 0;
  const sortedRacks = [...racksToPlace].sort((a, b) =>
    a.rackNumber.localeCompare(b.rackNumber, undefined, { numeric: true })
  );

  for (const wall of wallSegments) {
    if (rackIndex >= sortedRacks.length) break;

    let posAlongWall = 0;
    const isHorizontal = wall.direction === 'horizontal';

    while (rackIndex < sortedRacks.length && posAlongWall < wall.maxLength) {
      const rack = sortedRacks[rackIndex];
      const dim = dimensionsMap.get(rack.rackNumber);
      if (!dim) { rackIndex++; continue; }

      const rotated = getRotatedDimensions(dim, wall.facingRotation);
      const rackSizeAlongWall = isHorizontal ? rotated.width : rotated.depth;
      const rackSizeFromWall = isHorizontal ? rotated.depth : rotated.width;

      // Check if rack fits on remaining wall space
      if (posAlongWall + rackSizeAlongWall > wall.maxLength) break;

      let x: number, y: number;
      if (isHorizontal) {
        x = wall.startX + posAlongWall + rackSizeAlongWall / 2;
        y = wall.startY + rackSizeFromWall / 2;
        // For bottom wall (rotation 180), offset from bottom
        if (wall.facingRotation === 180) {
          y = wall.startY - rackSizeFromWall / 2;
        }
      } else {
        x = wall.startX + rackSizeFromWall / 2;
        y = wall.startY + posAlongWall + rackSizeAlongWall / 2;
        // For right wall (rotation 270), offset from right
        if (wall.facingRotation === 270) {
          x = wall.startX - rackSizeFromWall / 2;
        }
      }

      positions.push({
        rackNumber: rack.rackNumber,
        x: snapToGrid(x, gridUnit),
        y: snapToGrid(y, gridUnit),
        rotation: wall.facingRotation,
      });

      posAlongWall += rackSizeAlongWall + spacing;
      rackIndex++;
    }
  }

  return positions;
}

// ============================================
// Metrics Calculation
// ============================================

/**
 * Calculate layout metrics (space utilization, row count, aisle count).
 */
export function calculateLayoutMetrics(
  positions: PreviewRackPosition[],
  totalRacks: number,
  warehouseDimensions: WarehouseDimensions,
  dimensionsMap: Map<string, RackDimensions>,
  config: SmartLayoutConfig
): LayoutMetrics {
  const validPositions = positions.filter(p => p.isValid);
  const failedRacks = totalRacks - validPositions.length;

  // Calculate total rack footprint area
  let rackArea = 0;
  for (const pos of validPositions) {
    const dim = dimensionsMap.get(pos.rackNumber);
    if (dim) {
      const rotated = getRotatedDimensions(dim, pos.rotation);
      rackArea += rotated.width * rotated.depth;
    }
  }

  // Usable area (excluding wall margins)
  const usableLength = Math.max(1, warehouseDimensions.length - 2 * config.wallMargin);
  const usableWidth = Math.max(1, warehouseDimensions.width - 2 * config.wallMargin);
  const usableArea = usableLength * usableWidth;
  const spaceUtilization = usableArea > 0 ? rackArea / usableArea : 0;

  // Estimate row count from Y-positions
  const isHorizontal = config.orientation === 'horizontal';
  const rowCoords = new Set<number>();
  for (const pos of validPositions) {
    const coord = isHorizontal ? pos.y : pos.x;
    // Snap to 0.5m to group nearby racks into same row
    const snapped = Math.round(coord * 2) / 2;
    rowCoords.add(snapped);
  }
  const rowCount = rowCoords.size;

  // Aisle count = rows - 1 for parallel, or (rows / 2) for back-to-back
  const aisleCount = config.backToBack
    ? Math.max(0, Math.ceil(rowCount / 2) - 1) + (rowCount > 1 ? 1 : 0)
    : Math.max(0, rowCount - 1);

  return {
    totalRacks,
    placedRacks: validPositions.length,
    failedRacks,
    spaceUtilization: Math.min(1, spaceUtilization),
    aisleCount,
    rowCount,
  };
}

// ============================================
// Dimension Auto-Grow Helper
// ============================================

/**
 * Calculate minimum warehouse dimensions needed for the given layout config and rack count.
 * Used by QuickLayoutDialog to suggest/auto-grow warehouse dimensions.
 */
export function estimateRequiredDimensions(
  racksToPlace: RackSummary[],
  dimensionsMap: Map<string, RackDimensions>,
  config: SmartLayoutConfig
): { length: number; width: number } {
  if (racksToPlace.length === 0) return { length: 15, width: 10 };

  // Use average rack dimensions
  let totalWidth = 0, totalDepth = 0, count = 0;
  for (const rack of racksToPlace) {
    const dim = dimensionsMap.get(rack.rackNumber);
    if (dim) {
      totalWidth += dim.width;
      totalDepth += dim.depth;
      count++;
    }
  }
  if (count === 0) return { length: 15, width: 10 };

  const avgWidth = totalWidth / count;
  const avgDepth = totalDepth / count;

  // Estimate racks per row
  const racksPerRow = Math.max(1, Math.ceil(Math.sqrt(racksToPlace.length * 1.5)));
  const rowLength = racksPerRow * (avgWidth + config.rackSpacing) - config.rackSpacing;

  // Estimate number of rows
  const totalRows = Math.ceil(racksToPlace.length / racksPerRow);
  let rowPitch: number;
  if (config.backToBack) {
    rowPitch = avgDepth * 2 + config.backToBackGap + config.aisleWidth;
  } else {
    rowPitch = avgDepth + config.aisleWidth;
  }
  const totalRowHeight = totalRows * rowPitch;

  const isHorizontal = config.orientation === 'horizontal';
  const length = (isHorizontal ? rowLength : totalRowHeight) + 2 * config.wallMargin;
  const width = (isHorizontal ? totalRowHeight : rowLength) + 2 * config.wallMargin;

  return {
    length: Math.max(15, Math.ceil(length)),
    width: Math.max(10, Math.ceil(width)),
  };
}
